import React, { useEffect, useMemo } from "react";
import { times } from "lodash";

import {
  Box,
  Card,
  Chip,
  Typography,
  CardContent,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { Percent } from "@mui/icons-material";

import { useMap, MapboxGeoJSONFeature } from "react-map-gl";

import { HelpBox, NamedStatBox, MapSitesCard } from "@/components/atoms";
import { extractBreaks, getFillColor, toPercentage } from "@/utils";
import { LocationResult, PenPortraitInsightVariable, Site } from "@/types";

interface SiteInfo {name: string, latitude: number, longitude: number};

type OverlayProps = {
  sites: Site[];
  selectedSites: number[];
  scores: LocationResult;
  selected?: MapboxGeoJSONFeature;
  clearSelected: () => void;
  breaks: number[];
  firstTime: boolean;
  siteScores?: PenPortraitInsightVariable[];
  min: number;
  max: number;
  setSites: (sites: number[]) => void;
  siteInfo: SiteInfo | null;
  onSiteHover: (site_id: number | null) => void;
  onSiteDelete: (site_id: number) => void;
};

export const Overlay: React.FC<OverlayProps> = ({
  sites,
  selectedSites,
  scores,
  selected,
  clearSelected,
  breaks,
  firstTime,
  siteScores,
  min,
  max,
  setSites,
  siteInfo,
  onSiteHover,
  onSiteDelete
}) => {
  const { current: map } = useMap();
  const theme = useTheme();

  const selectedSiteObjects = useMemo(() => {
    const selectedSet = new Set(selectedSites);
    return sites.filter((site) => selectedSet.has(site.site_id));
  }, [sites, selectedSites]);

  useEffect(() => {
    if (!firstTime) {
      map && map.isStyleLoaded() && resetMap();
      map &&
        scores.area.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "area-base",
            "fill-color",
            getFillColor(extractBreaks(scores.area), theme.palette.gradient),
          );
      map &&
        scores.district.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "district-source",
              sourceLayer: "postal_district_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "district-base",
            "fill-color",
            getFillColor(extractBreaks(scores.district), theme.palette.gradient),
          );
      map &&
        scores.sector.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "sector-source",
              sourceLayer: "postal_sector_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "sector-base",
            "fill-color",
            getFillColor(extractBreaks(scores.sector), theme.palette.gradient),
          );
    }
  }, [firstTime, map, scores]);

  const resetMap = () => {
    clearSelected();
    map &&
      [...times(124)].forEach((value) => {
        map.setFeatureState(
          { id: value, source: "area-source", sourceLayer: "postal_area_test" },
          { score: null, name: null, level: null },
        );
      });
    map &&
      [...times(2839)].forEach((value) => {
        map.setFeatureState(
          {
            id: value,
            source: "district-source",
            sourceLayer: "postal_district_test",
          },
          { score: null, name: null, level: null },
        );
      });
    map &&
      [...times(9576)].forEach((value) => {
        map.setFeatureState(
          {
            id: value,
            source: "sector-source",
            sourceLayer: "postal_sector_test",
          },
          { score: null, name: null, level: null },
        );
      });
  };

  return (
    <Grid container size={12} spacing={2} height="100%" width="100%" p={2}>
      <Grid size={8}>
        {selected && (
          <Box
            sx={{
              backgroundColor: theme.palette.background.default,
              position: "absolute",
              bottom: 80,
              left: 12,
            }}
          >
            <NamedStatBox
              title={selected.state?.name || ""}
              name="Penetration"
              value={selected.state?.score || 0}
              valueFormatter={toPercentage}
              icon={<Percent fontSize="medium" color="secondary" />}
              description="The percentage of postcodes in this location that your audience covers"
            />
          </Box>
        )}
        {breaks.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 40,
              left: 12,
            }}
          >
            <Box
              sx={{
                width: "150px",
                height: "10px",
                borderRadius: "10px",
                background:
                  `linear-gradient(to right, ${theme.palette.gradient[0]}, ${theme.palette.gradient[1]}, ${theme.palette.gradient[3]}, ${theme.palette.gradient[4]})`,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption">{toPercentage(0, 0)}</Typography>
              <Typography variant="caption">
                {toPercentage(breaks[0], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[1], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[2], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[3], 0)}
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>
      <Grid size={4}>
          {siteScores && (
            <>
              <MapSitesCard
                title={'Top Sites'}
                variables={siteScores}
                score={'score_wt'}
                min={min}
                max={max}
                siteInfo={siteInfo}
                onSiteHover={onSiteHover}
                onSiteDelete={onSiteDelete}
              />
              <Card
                sx={{
                  mt: 2,
                  borderRadius: 1,
                  borderColor: theme.palette.divider,
                  width: "100%",
                  position: "relative",
                  // height: "40",
                  bgColor: theme.palette.background.paper
                }}
              >
                <HelpBox title="Add Sites">
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      pb: 2,
                      "&:last-child": { pb: 2 },
                      gap: 1
                    }}
                  >
                    <Box width="100%">
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disableClearable
                        options={sites}
                        value={selectedSiteObjects}
                        getOptionLabel={(option) => option.site_name}
                        isOptionEqualToValue={(option, value) => option.site_id === value.site_id}
                        filterOptions={(options, state) => {
                          const selectedSet = new Set(selectedSites);
                          const unselected = options.filter((site) => !selectedSet.has(site.site_id));

                          // Also apply standard search text matching
                          if (!state.inputValue) return unselected;
                          const search = state.inputValue.toLowerCase();
                          return unselected.filter((site) =>
                            site.site_name.toLowerCase().includes(search)
                          );
                        }}

                        onChange={(_, newValue) => {
                          const updatedIds = newValue.map((site) => site.site_id);
                          setSites(updatedIds);
                        }}

                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label="Search & Add Sites"
                            variant="outlined"
                            color="secondary"
                            size="small"
                          />
                        )}

                        renderTags={() => null}
                      />
                    </Box>
                    {(selectedSites.length !== sites.length) ? (
                      <Box display="flex" justifyContent="flex-end">
                        <Chip
                          label='Select All'
                          size='small'
                          variant='filled'
                          color='secondary'
                          onClick={() => setSites(sites.map(x => x.site_id))}
                        />
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="flex-end">
                        <Chip
                          label='Clear All'
                          size='small'
                          variant='filled'
                          color='secondary'
                          onClick={() => setSites([])}
                        />
                      </Box>
                    )}
                  </CardContent>
                </HelpBox>
              </Card>
            </>
          )}
      </Grid>
    </Grid>
  );
};
